import { stateAbbreviations } from 'constants/jobLocations';

// Real CMS content tags grade/location with a mix of full display names ("New Zealand",
// "Resident Medical Officer") and short abbreviations ("NZ", "RMO"), while the search context
// passed in from the page is always a full display name. Plain substring matching between the
// two produces both false positives (tag "au" is a substring of unrelated words like "auckland")
// and false negatives (tag "nz" is not a substring of "new zealand" - there's no "nz" run of
// characters in "new zealand"), so short/known abbreviations are matched via this explicit table
// instead of by containment.
const TERM_SYNONYMS: Record<string, string[]> = {
  au: ['australia'],
  australia: ['au'],
  nz: ['new zealand'],
  'new zealand': ['nz'],
  rmo: ['resident medical officer'],
  'resident medical officer': ['rmo'],
  smo: ['senior medical officer'],
  'senior medical officer': ['smo'],
  cd: ['clinical director'],
  'clinical director': ['cd'],
  dms: ['director of medical services'],
  doms: ['director of medical services'],
  'director of medical services': ['dms', 'doms'],
  // Confirmed against real CMS testimonial content: state-level placements are routinely
  // written as an abbreviation in job_title (e.g. "St George Private Hospital, Kogarah, NSW"),
  // which without this table wouldn't match a "New South Wales" location search at all -
  // derived from the same stateAbbreviations map jobListingHelper uses for breadcrumbs, so the
  // two can't drift onto different abbreviation sets.
  ...Object.entries(stateAbbreviations).reduce<Record<string, string[]>>(
    (synonyms, [fullName, abbreviation]) => {
      const normalizedFullName = fullName.toLowerCase();
      const normalizedAbbreviation = abbreviation.toLowerCase();
      synonyms[normalizedFullName] = [normalizedAbbreviation];
      synonyms[normalizedAbbreviation] = [normalizedFullName];
      return synonyms;
    },
    {},
  ),
};

// A state/territory also broadly implies its country (e.g. a "New South Wales" search is still
// somewhat relevant to generic "AU"-tagged content), but only as a *weaker* fallback signal, not
// an equal synonym: an item mentioning "AU" doesn't say anything about NSW specifically. Kept
// separate from TERM_SYNONYMS (whose entries are treated as exact equivalents, e.g. "NSW" IS
// "New South Wales") so a genuinely NSW-tagged item still outranks a merely AU-tagged one -
// if they were equal, generic AU-tagged content (much more common than state-specific content)
// could crowd the actually-relevant state match out of a capped result by coincidental id order.
const BROADER_TERM_SYNONYMS: Record<string, string[]> = Object.entries(stateAbbreviations).reduce<
  Record<string, string[]>
>((broader, [fullName, abbreviation]) => {
  broader[fullName.toLowerCase()] = ['au', 'australia'];
  broader[abbreviation.toLowerCase()] = ['au', 'australia'];
  return broader;
}, {});

const normalize = (value: string) => value.trim().toLowerCase();

// The "SMO/MOSS" grade is stored and searched as a single combined literal (see JobGrade in
// types/jobs.ts) - every other grade value is a plain full name - but CMS content and this
// synonym table only know about its individual parts ("SMO", "Senior Medical Officer"). Without
// splitting on "/", "smo/moss" as a whole never matches the "smo" synonym entry at all, so a
// grade="SMO/MOSS" search would silently fail to match any content tagged "SMO" or
// "Senior Medical Officer".
export const expandSynonyms = (value: string): string[] => {
  const normalized = normalize(value);
  const parts = normalized.includes('/') ? normalized.split('/').filter(Boolean) : [normalized];
  const variants = new Set<string>([normalized, ...parts]);
  parts.forEach(part => {
    (TERM_SYNONYMS[part] ?? []).forEach(synonym => variants.add(synonym));
  });
  return [...variants];
};

const EXACT_MATCH_WEIGHT = 1;
const BROAD_MATCH_WEIGHT = 0.5;

/**
 * Scores how well a single tag matches a single search term: a full point for an exact/synonym
 * match (e.g. tag "nsw" vs term "New South Wales"), a half point for a broader country-level
 * fallback match (e.g. tag "au" vs term "New South Wales" - relevant, but less specific), or 0
 * for no relationship at all (e.g. tag "nz" vs term "New South Wales").
 */
const matchWeight = (tag: string, term: string): number => {
  const tagVariants = expandSynonyms(tag);
  const termVariants = expandSynonyms(term);
  if (tagVariants.some(variant => termVariants.includes(variant))) {
    return EXACT_MATCH_WEIGHT;
  }

  const broaderTermVariants = BROADER_TERM_SYNONYMS[normalize(term)] ?? [];
  if (tagVariants.some(variant => broaderTermVariants.includes(variant))) {
    return BROAD_MATCH_WEIGHT;
  }

  return 0;
};

/**
 * Splits and lowercases a comma-separated tag field, e.g. "AU, Sydney" -> ["au", "sydney"]. Any
 * free-text field being matched against a search context should go through this rather than
 * being pushed in as one raw string, so a multi-part value like job_title "RMO, Internal
 * Medicine" can still match on just "RMO".
 */
export const splitTagList = (value: string) =>
  value
    .split(',')
    .map(tag => tag.trim().toLowerCase())
    .filter(Boolean);

/**
 * Splits and lowercases the comma-separated `category`/`subcategory` tag fields shared by every
 * CMS content type that carries them (FAQs, testimonials), e.g. "AU, Sydney" -> ["au", "sydney"].
 */
export const buildTagsFromCategoryFields = (category?: string, subcategory?: string): string[] =>
  [category, subcategory].filter((value): value is string => !!value).flatMap(splitTagList);

export type PrioritisationResult<T> = {
  items: T[];
  // Whether at least one item actually matched the active context - false when every item in
  // `items` is unmatched filler shown only to avoid an empty section.
  hasContextMatch: boolean;
};

/**
 * Ranks items by how well they match the active search context terms, most-matched first,
 * without dropping non-matching items - matching is a prioritisation, not a hard filter, since
 * real CMS content isn't reliably tagged with grade/speciality/location terms (fixed audience
 * labels, abbreviations, or missing entirely), so a hard filter would too often leave nothing to
 * show. Matching compares exact normalized values (through the abbreviation table above) rather
 * than raw substring containment.
 *
 * Each term contributes its single best-matching tag's weight (see matchWeight) to an item's
 * score, so an item tagged with both the active grade and speciality outranks one matching only
 * grade, which in turn outranks one matching nothing - callers don't need to special-case
 * "grade+speciality present" vs "speciality absent, just use grade": whichever context terms are
 * actually provided are what gets ranked on. A state-specific term additionally credits generic
 * country-tagged content at a lower weight, so e.g. a "New South Wales" search doesn't leave an
 * unrelated "NZ"-tagged item ranked level with (or above, by coincidental array order) an
 * "AU"-tagged one - while a genuinely NSW-tagged item still outranks both.
 *
 * Shared by filterFaqsForContext and filterTestimonialsForContext so the two can't drift onto
 * different (and differently buggy) matching rules.
 */
export const prioritiseByContext = <T>(
  items: T[],
  contextTerms: string[],
  getTags: (item: T) => string[],
): PrioritisationResult<T> => {
  if (!contextTerms.length) {
    return { items, hasContextMatch: false };
  }

  const scoreItem = (item: T): number => {
    const tags = getTags(item);
    return contextTerms.reduce((total, term) => {
      const bestWeightForTerm = Math.max(0, ...tags.map(tag => matchWeight(tag, term)));
      return total + bestWeightForTerm;
    }, 0);
  };

  const decorated = items.map(item => ({ item, score: scoreItem(item) }));
  // Array#sort is a stable sort (guaranteed since ES2019), so items with an equal score keep
  // their original relative order rather than being shuffled.
  decorated.sort((a, b) => b.score - a.score);

  return {
    items: decorated.map(({ item }) => item),
    // A broad (country-level fallback) match alone isn't enough to claim a real match - e.g. a
    // "New South Wales" search matching only AU-tagged testimonials shouldn't flip this to true,
    // since DoctorStories renders a "...across New South Wales" claim off this flag that a merely
    // AU-tagged card doesn't support. Only an exact/synonym match counts.
    hasContextMatch: decorated.some(({ score }) => score >= EXACT_MATCH_WEIGHT),
  };
};
